/**
 * <AISafetyBadge/>
 *
 * Trust signal rendered NEAR (or inline with) any AI clinical output in the
 * UI. Looks up the surface by `surface_id` against the AI_SURFACES registry
 * and renders the current validation state:
 *
 *   - clinical_validation populated AND not expired → blue "Clinician-validated · {date}"
 *   - clinical_validation populated AND expired      → amber "Clinician-validated (refresh due)"
 *   - clinical_validation null                       → "AI-assisted · Validation pending"
 *
 * "Validation pending" is NOT a deceptive placeholder — it tells the
 * clinician honestly that this surface is AI-generated and has not yet
 * been formally signed off. Healthcare clinicians actively look for this
 * disclosure; hiding it would damage trust more than showing it.
 *
 * Click opens a Dialog with the per-surface details:
 *   - Surface name + risk class
 *   - Current guardrails in effect
 *   - Validator credentials (if signed) and cases reviewed
 *   - Link to the public /ai-safety page
 */
import React, { useState } from 'react';
import { Brain, BadgeCheck, AlertTriangle, ShieldCheck, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AI_SURFACE_BY_ID } from '../../data/ai_surfaces';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';

const RISK_CLASS_META = {
  high:   { label: 'High clinical-risk surface', tone: 'text-rose-700 dark:text-rose-300' },
  medium: { label: 'Medium clinical-risk surface', tone: 'text-amber-700 dark:text-amber-300' },
  low:    { label: 'Low clinical-risk surface', tone: 'text-cn-text-muted' },
};

export function AISafetyBadge({ surfaceId, compact = false }) {
  const [open, setOpen] = useState(false);
  const surface = AI_SURFACE_BY_ID[surfaceId];
  if (!surface) return null;

  const validation = surface.clinical_validation;
  const expired = validation?.expires_at ? new Date(validation.expires_at) < new Date() : false;

  let state, icon, toneCls, label;
  if (validation && !expired) {
    state = 'signed';
    icon = BadgeCheck;
    toneCls = 'text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-950/30';
    const signed = new Date(validation.signed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    label = `Clinician-validated · ${signed}`;
  } else if (validation && expired) {
    state = 'expired';
    icon = AlertTriangle;
    toneCls = 'text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30';
    label = 'Clinician-validated (refresh due)';
  } else {
    state = 'pending';
    icon = Brain;
    toneCls = 'text-cn-text-muted hover:bg-cn-bone-50 dark:hover:bg-cn-ink-800';
    label = 'AI-assisted · Validation pending';
  }
  const Icon = icon;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 ${compact ? 'text-[11px]' : 'text-xs'} px-2 py-0.5 rounded-full border border-cn-border ${toneCls} focus:outline-none focus:ring-2 focus:ring-cn-primary/40 transition-colors`}
        data-testid={`ai-safety-badge-${surfaceId}`}
        data-state={state}
      >
        <Icon className="w-3 h-3" />
        <span className="font-semibold">{label}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="ai-safety-modal">
          <DialogHeader>
            <div className="inline-flex w-12 h-12 rounded-2xl bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 items-center justify-center mb-2">
              <Brain className="w-5 h-5" />
            </div>
            <DialogTitle className="text-xl font-display">{surface.name}</DialogTitle>
            <DialogDescription className="text-sm text-cn-text-muted">
              {surface.surface_label} · <span className={RISK_CLASS_META[surface.risk_class]?.tone}>{RISK_CLASS_META[surface.risk_class]?.label}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1.5">Active guardrails</div>
              <ul className="space-y-1">
                {surface.guardrails.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-cn-text dark:text-cn-bone-50">
                    <ShieldCheck className="w-3.5 h-3.5 mt-1 shrink-0 text-emerald-600" />
                    <span className="leading-snug">{g}</span>
                  </li>
                ))}
              </ul>
            </div>

            {validation ? (
              <div className="cn-card p-3 bg-sky-50 dark:bg-sky-950/30 border-sky-200/60 dark:border-sky-800/40">
                <div className="text-[11px] uppercase tracking-wider font-bold text-sky-700 dark:text-sky-300 mb-1">Validator</div>
                <div className="font-display font-bold text-cn-text dark:text-white" data-testid="ai-safety-validator-name">
                  {validation.reviewer_name}
                </div>
                <div className="text-xs text-cn-text-muted mt-0.5">
                  NPI {validation.npi} · {validation.cases_reviewed} cases reviewed · {Math.round((validation.pass_rate || 0) * 100)}% pass-rate
                </div>
                <div className="text-[10px] text-cn-text-muted mt-1">
                  Signed {new Date(validation.signed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                  {' · Refresh due '}{new Date(validation.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
            ) : (
              <div className="cn-card p-3 bg-cn-bone-50 dark:bg-cn-ink-900 border-cn-border">
                <div className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1">Validation status</div>
                <div className="text-sm text-cn-text dark:text-cn-bone-50 leading-snug">
                  This AI surface is in active clinical-validation review. Until a licensed clinician
                  formally signs off, the output here should be treated as a draft and verified by the
                  clinician who signs the final record.
                </div>
              </div>
            )}

            <Link
              to="/ai-safety"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1 text-sm font-semibold text-cn-primary hover:underline"
              data-testid="ai-safety-page-link"
            >
              View full AI safety posture <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AISafetyBadge;
